import React, { useEffect, useMemo } from 'react'
import { TableProps } from '../types'
import { useAppSelector } from '@shared/hooks/useAppSelector'
import EditableCell from '../EditableCell'
import styles from '../Table.module.scss'
import { useConfigColumns } from '../libs/utils'
import { Table as CoreTable } from 'antd'
import { useDispatch } from 'react-redux'
import { setSelectedRowKeys } from 'store/actions/table'
import { useTranslation } from 'react-i18next'
import { useVT } from 'virtualizedtableforantd4';
import EditableRow from '../EditableRow'

export function Table<T extends object>({
  editColumns,
  dataSource,
  selected,
  hideSelectAll,
  tableKey,
  editable = false,
  virtual = false,
  onClickRow,
  scroll,
  ...rest
}: TableProps<T>) {
  const selectedRowKeys = useAppSelector((state) => state.table.selectedRowKeys)
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { columns, setFilteredInfo, sortedData } = useConfigColumns(
    editColumns,
    dataSource
  )

  const [ vt, setComponent ] = useVT(() => ({ scroll: { y: scroll?.y } }), []);

  useMemo(() => setComponent({
    body: {
      cell: EditableCell,
      row: EditableRow
    },
  }), [setComponent]);

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    dispatch(setSelectedRowKeys({ [tableKey]: newSelectedRowKeys }))
  }

  const handleChange: TableProps<T>['onChange'] = (
    pagination,
    filters,
    sorter
  ) => {
    setFilteredInfo(filters)
  }

  useEffect(() => {
    if (!(tableKey in selectedRowKeys)) {
      setSelectedRowKeys({ [tableKey]: [] })
    }
  }, [])

  return (
    <CoreTable
      locale={{ emptyText: t('orders.salaryCardOrder.tableTitles.emptyTable') }}
      { ...( editable && {
        components: virtual ? vt : {
          body: {
            cell: EditableCell,
            row: EditableRow
          }
        }
      }) }
      className={styles.table}
      columns={columns}
      onRow={(data, index) => {
        return {
          data,
          onClick: () => onClickRow && onClickRow(data),
          className: onClickRow ? styles.cursor : ''
        }
      }}
      onChange={handleChange}
      dataSource={sortedData}
      {...(selected
        ? {
            rowSelection: {
              onChange: onSelectChange,
              selectedRowKeys: selectedRowKeys[tableKey],
              hideSelectAll,
              type: hideSelectAll ? 'radio' : 'checkbox',
            },
          }
        : {})}
      scroll={scroll}
      {...rest}
    />
  )
}
